import { useState, useEffect, useMemo } from "react";
import Avatar from "react-avatar";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";
import { map } from "lodash";
import { updateConversation } from "actions/conversationActions";
import cx from "classnames";
import { getProductTikToks } from "actions/productTikTokActions";
import useCallApi from "hook/useCallApi";
import { sendMessage } from "actions/messageActions";
import SelectBox from "components/elements/selectBox";

const Detail = ({ conversation, onClose, updateConversation, reloadConversation, getProductTikToks, send }) => {
    const [showNote, setShowNote] = useState(false);
    const [note, setNote] = useState("");
    const [tab, setTab] = useState("order");
    const [search, setSearch] = useState("");
    const [orderStatus, setOrderStatus] = useState("");
    const searchOptions = useMemo(
        () => ({
            page: 1,
            sizeperpage: 10,
            filter: { shops: conversation.shop._id },
            search,
        }),
        [conversation, search]
    );
    const { data, loading } = useCallApi(getProductTikToks, searchOptions);
    const products = useMemo(() => {
        return data?.productTikToks || [];
    }, [data]);

    useEffect(() => {
        setNote(conversation?.note || "");
    }, [conversation]);

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
    };

    const onClickSendMessage = (message) => {
        send(message, "PRODUCT_CARD");
    };

    return (
        <div className="flex-column flex-lg-row-auto w-100 w-lg-300px w-xl-400px mb-10 mb-lg-0">
            <div className="card card-flush h-100">
                <div className="p-3 h-100" style={{ overflow: "auto" }}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="d-flex align-items-center position-relative w-100">
                            <span role="button" className="position-absolute top-0 end-0" onClick={onClose}>
                                <i className="ki-outline ki-cross" style={{ fontSize: "30px" }}></i>
                            </span>
                            <div>
                                <div className="symbol symbol-45px symbol-circle d-flex gap-4 align-items-center">
                                    {conversation?.participants?.[1]?.avatar ? (
                                        <img src={conversation?.participants?.[1]?.avatar} alt="" />
                                    ) : (
                                        <Avatar
                                            name={conversation?.participants?.[1]?.nickname}
                                            size="45"
                                            round={true}
                                        />
                                    )}
                                    <div>
                                        <h3 className="text-decoration-none d-block">
                                            {conversation?.participants?.[1]?.nickname || "unknow"}
                                        </h3>
                                        <span className="badge badge-info">{conversation.shop?.name}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="my-4">
                        <span
                            role="button"
                            className="text-decoration-none text-info d-inline-block"
                            onClick={() => setShowNote(true)}
                        >
                            Add a note
                        </span>
                        {showNote && (
                            <>
                                <div className="form-group mt-4">
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        placeholder="Note"
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                    ></textarea>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-info mt-4"
                                    onClick={() =>
                                        updateConversation({ id: conversation._id, note }).then(() => {
                                            reloadConversation();
                                        })
                                    }
                                >
                                    Save
                                </button>
                            </>
                        )}
                    </div>
                    <ul className="nav nav-tabs mb-3">
                        <li className="nav-item" onClick={() => setTab("order")}>
                            <span role="button" className={cx("nav-link", { active: tab === "order" })}>
                                Orders
                            </span>
                        </li>
                        <li className="nav-item" onClick={() => setTab("product")}>
                            <span role="button" className={cx("nav-link", { active: tab === "product" })}>
                                Products
                            </span>
                        </li>
                        <li className="nav-item" onClick={() => setTab("coupons")}>
                            <span role="button" className={cx("nav-link", { active: tab === "coupons" })}>
                                Coupons
                            </span>
                        </li>
                    </ul>

                    {tab === "order" && (
                        <>
                            <div>
                                <SelectBox
                                    options={[
                                        { value: "", label: "All" },
                                        { value: "1", label: "To Ship" },
                                        { value: "2", label: "Shipped" },
                                        { value: "3", label: "Completed" },
                                    ]}
                                    name="order_status"
                                    value={orderStatus}
                                    onChange={(data) => {
                                        setOrderStatus(data?.value || "");
                                    }}
                                    isClearable={false}
                                />
                            </div>

                            {conversation?.orders
                                ?.filter((order) => {
                                    if (orderStatus === "1") return ["UNPAID", "ON_HOLD"].includes(order.status);
                                    if (orderStatus === "2")
                                        return [
                                            "AWAITING_SHIPMENT",
                                            "AWAITING_COLLECTION",
                                            "IN_TRANSIT",
                                            "PARTIALLY_SHIPPING",
                                        ].includes(order.status);
                                    if (orderStatus === "3") return ["DELIVERED"].includes(order.status);
                                    if (orderStatus === "4") return ["COMPLETED", "CANCELLED"].includes(order.status);
                                    return true;
                                })
                                ?.map((order) => (
                                    <div className="order-box rounded border mb-3 p-4" key={order._id}>
                                        <div className="d-flex justify-content-between mb-2">
                                            <div className="text-muted">Delivered</div>
                                            <div className="text-muted small text-end">
                                                <div>
                                                    #{order.apiOrderId}{" "}
                                                    <span
                                                        role="button"
                                                        className="bi bi-clipboard ms-1"
                                                        onClick={() => handleCopy(order.apiOrderId)}
                                                    />
                                                </div>
                                                <div>
                                                    {moment
                                                        .unix(Math.floor(+(order?.create_time || Date.now() / 1000)))
                                                        .fromNow()}
                                                </div>
                                            </div>
                                        </div>
                                        {order.line_items.map((item) => (
                                            <div key={item.id} className="bg-white p-2 rounded border mb-2 p-2">
                                                <div className="d-flex align-items-center gap-4">
                                                    <div style={{ minWidth: 60, height: 60, backgroundColor: "#ddd" }}>
                                                        <img
                                                            src={item.sku_image}
                                                            style={{
                                                                width: "100%",
                                                                height: "100%",
                                                                objectFit: "cover",
                                                            }}
                                                        />
                                                    </div>
                                                    <div>
                                                        <div>
                                                            <strong>{item.product_name}</strong>
                                                        </div>
                                                        <div className="small text-muted">
                                                            Order Id: {order.apiOrderId}
                                                        </div>
                                                        <div className="small text-muted">
                                                            Seller SKU: {item.sku_name}
                                                        </div>
                                                        <div>
                                                            <span
                                                                role="button"
                                                                className="text-decoration-none text-primary small"
                                                            >
                                                                {`${order.payment.total_amount} ${order.payment.currency}`}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="text-muted">Total</div>
                                            <div className="fw-bold">
                                                {map(
                                                    conversation.orders.reduce((result, order) => {
                                                        result[order.payment.currency] = (
                                                            (result[order.payment.currency] || 0) +
                                                            (+order?.payment?.total_amount || 0)
                                                        ).toFixed(2);
                                                        return result;
                                                    }, {}),
                                                    (value, key) => (
                                                        <span key={key}>
                                                            {value} {key}
                                                        </span>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                        </>
                    )}

                    {tab === "product" && (
                        <div>
                            <div className="w-100 position-relative">
                                <i className="ki-outline ki-magnifier fs-3 text-gray-500 position-absolute top-50 ms-5 translate-middle-y"></i>
                                <input
                                    type="text"
                                    className="form-control form-control-solid px-13"
                                    placeholder="Search by product..."
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            <div>
                                {loading ? (
                                    <p>Loading</p>
                                ) : (
                                    products.map((product) => (
                                        <div key={product._id} className="bg-white p-2 rounded border mb-2 p-2">
                                            <div className="d-flex align-items-center gap-4">
                                                <div style={{ minWidth: 60, height: 60, backgroundColor: "#ddd" }}>
                                                    <img
                                                        src={product.main_images?.[0]}
                                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                    />
                                                </div>
                                                <div>
                                                    <div>
                                                        <strong>{product.title}</strong>
                                                    </div>
                                                    <div>
                                                        <span
                                                            role="button"
                                                            className="text-decoration-none text-primary small"
                                                        >
                                                            ${`${product.price} $`}
                                                        </span>
                                                        <div className="mt-4 d-flex gap-4">
                                                            <Link
                                                                to={
                                                                    "https://shop.tiktok.com/view/product/" +
                                                                    product.apiProductId
                                                                }
                                                                className="btn btn-light btn-sm"
                                                                target="_blank"
                                                            >
                                                                View Detail
                                                            </Link>
                                                            <button
                                                                type="button"
                                                                className="btn btn-info btn-sm"
                                                                onClick={() => onClickSendMessage(product.apiProductId)}
                                                            >
                                                                Send
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}

                    {tab === "coupons" && (
                        <div className="h-100">
                            <div className="w-100 position-relative">
                                <i className="ki-outline ki-magnifier fs-3 text-gray-500 position-absolute top-50 ms-5 translate-middle-y"></i>
                                <input
                                    type="text"
                                    className="form-control form-control-solid px-13"
                                    placeholder="Search by coupon..."
                                />
                            </div>
                            <div className="d-flex justify-content-center align-items-center mt-4 h-50">
                                <button className="btn btn-info mt-4">Create Coupon</button>
                            </div>
                        </div>
                    )}

                    {/* <div className="d-flex justify-content-end">
                        <button className="btn btn-outline-secondary btn-sm me-2">Copy link</button>
                        <button className="btn btn-primary btn-sm">Send</button>
                    </div> */}
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({});
const mapDispatchToProps = {
    updateConversation,
    getProductTikToks,
    sendMessage,
};

export default connect(mapStateToProps, mapDispatchToProps)(Detail);
